import { FC, useState } from 'react';
import { Button, Skeleton, Typography } from '@mui/material';
import moment from 'moment';
import useGetCompanyNews from 'services/hooks/companies/useGetCompanyNews';
import { SacraTimestamp } from '../../CompanyOverview.styles';
import {
  ArticleLink,
  ArticleList,
  ArticleMeta,
  MetaDot,
  NewsItem,
  NewsList,
  NewsMeta,
  NewsSummary,
  NewsTitle,
  PaginationRow,
} from '../NewsAndPressReleasesCard.styles';
import { SacraPressReleasesCardProps } from './SacraPressReleasesCard.props';

const SacraPressReleasesCard: FC<SacraPressReleasesCardProps> = ({ companyId }) => {
  const [pageAfter, setPageAfter] = useState<string | undefined>(undefined);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  const { data, isLoading, isFetching } = useGetCompanyNews(companyId, pageAfter);
  const busy = isLoading || isFetching;
  const hasData = (data?.results.length ?? 0) > 0;

  const handleNext = () => {
    if (!data?.next_cursor) return;
    setCursorHistory(prev => [...prev, pageAfter ?? '']);
    setPageAfter(data.next_cursor ?? undefined);
  };

  const handlePrev = () => {
    const history = [...cursorHistory];
    const prev = history.pop();
    setCursorHistory(history);
    setPageAfter(prev || undefined);
  };

  if (busy) {
    return (
      <NewsList>
        {[0, 1, 2, 3].map(i => (
          <NewsItem key={i}>
            <Skeleton variant="text" width="90%" height={16} />
            <Skeleton variant="text" width="40%" height={12} />
            <Skeleton variant="text" width="100%" height={12} />
          </NewsItem>
        ))}
      </NewsList>
    );
  }

  if (!hasData) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
        No news available for this company.
      </Typography>
    );
  }

  return (
    <>
      {data?.sacra_fetched_at && (
        <SacraTimestamp sx={{ mb: 1 }}>fetched {new Date(data.sacra_fetched_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</SacraTimestamp>
      )}
      <NewsList>
        {data!.results.map((item, i) => (
          <NewsItem key={i}>
            <NewsTitle as="span">{item.headline}</NewsTitle>
            <NewsMeta>
              {item.release_date && <span>{moment(item.release_date).format('MMM D, YYYY')}</span>}
            </NewsMeta>
            {item.description && <NewsSummary>{item.description}</NewsSummary>}
            {item.articles && item.articles.length > 0 && (
              <ArticleList>
                {item.articles.map((article, j) => (
                  <div key={j}>
                    <ArticleLink href={article.link} target="_blank" rel="noopener noreferrer">
                      {article.headline ?? article.link}
                    </ArticleLink>
                    {(article.publication || article.date_published) && (
                      <ArticleMeta>
                        {' — '}
                        {article.publication}
                        {article.publication && article.date_published && <MetaDot> · </MetaDot>}
                        {article.date_published && moment(article.date_published).format('MMM D, YYYY')}
                      </ArticleMeta>
                    )}
                  </div>
                ))}
              </ArticleList>
            )}
          </NewsItem>
        ))}
      </NewsList>

      {(cursorHistory.length > 0 || data!.next_cursor) && (
        <PaginationRow>
          <Button size="small" disabled={cursorHistory.length === 0} onClick={handlePrev}>
            Previous
          </Button>
          <Button size="small" disabled={!data!.next_cursor} onClick={handleNext}>
            Next
          </Button>
        </PaginationRow>
      )}
    </>
  );
};

export default SacraPressReleasesCard;
